/**
 * Album wall identity, grid fingerprint, and row dedupe — split from libraryAlbumIndex (#916 size gate).
 */
import { groupDiscFolderSiblings } from '../utils/discFolderGroup';
import {
  isLocalLibraryAlbumKey,
  normalizeLocalAlbumId,
} from './localLibraryMetadata';

export type AlbumIndexRow = Record<string, unknown>;

export type AlbumIndexDedupeResult = {
  albums: AlbumIndexRow[];
  collapsed: number;
};

/** Grid-visible slice of one album row (id, title, cover hashes). */
export function albumRowGridSlice(a: AlbumIndexRow | null | undefined): string {
  if (!a) return '';
  const id = String(a.album_key ?? a.id ?? '').trim();
  const title = String(a.title ?? a.album ?? '').trim();
  const embedded = String(a.embedded_cover_hash ?? '').trim();
  const folder = String(a.folder_cover_hash ?? '').trim();
  return `${id}\u001f${title}\u001f${embedded}\u001f${folder}`;
}

export function computeAlbumGridFingerprint(albums: AlbumIndexRow[]): string {
  if (!albums?.length) return '0';
  const slices = albums.map((a) => albumRowGridSlice(a)).sort();
  return `${albums.length}:${slices.join('\u001e')}`;
}

/** Compare warm index fingerprint to a fresh catalog slice — detects id drift at stable counts. */
export function gridFingerprintDrifted(
  warmFingerprint: string | null | undefined,
  freshAlbums: AlbumIndexRow[],
): boolean {
  if (!warmFingerprint || !freshAlbums?.length) return false;
  return computeAlbumGridFingerprint(freshAlbums) !== warmFingerprint;
}

function normalizeTitle(value: unknown): string {
  return String(value ?? '')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, ' ');
}

function normalizeArtist(value: unknown): string {
  let s = normalizeTitle(value);
  if (s.startsWith('the ')) s = s.slice(4);
  return s;
}

/**
 * Engine canonical album identity for grid tiles — album_key (stable_id) when present,
 * else normalized id. Matches get_albums: one canonical album = one stable hash.
 */
export function resolveCanonicalAlbumTileId(a: AlbumIndexRow | null | undefined): string {
  if (!a) return '';
  const fromKey = String(a.album_key ?? '').trim();
  if (fromKey) {
    return isLocalLibraryAlbumKey(fromKey) ? normalizeLocalAlbumId(fromKey) : fromKey;
  }
  const fromId = String(a.id ?? '').trim();
  if (!fromId) return '';
  return isLocalLibraryAlbumKey(fromId) ? normalizeLocalAlbumId(fromId) : fromId;
}

/** Canonical identity for index dedupe — stable id when present, else title+album_artist+year. */
export function albumIdentityKey(a: AlbumIndexRow | null | undefined): string {
  if (!a) return '';
  const stable = resolveCanonicalAlbumTileId(a);
  if (stable) return stable;
  const title = normalizeTitle(a.title ?? a.album);
  if (!title) return '';
  const artist = normalizeArtist(
    a.album_artist ?? a.albumArtist ?? a.artist_name ?? a.artist,
  );
  const year = a.year != null && a.year !== '' ? String(a.year) : '';
  return `${artist}\u001f${title}\u001f${year}`;
}

function albumRowScore(a: AlbumIndexRow): number {
  let score = Number(a.track_count) || 0;
  if (a.embedded_cover_hash || a.folder_cover_hash) score += 1000;
  if (a.cover_art_url && String(a.cover_art_url).trim()) score += 500;
  if (a.track_count && Number(a.track_count) > 0) score += Number(a.track_count);
  const bd = Number(a.bit_depth) || 0;
  const sr = Number(a.sample_rate) || 0;
  if (bd && sr) score += bd * 10 + Math.round(sr / 1000);
  else if (a.format && String(a.format).trim()) score += 50;
  return score;
}

/** Merge duplicate index rows — keep richest metadata and aggregate quality for badges. */
function mergeAlbumIndexRowPair(primary: AlbumIndexRow, secondary: AlbumIndexRow): AlbumIndexRow {
  const richer = albumRowScore(primary) >= albumRowScore(secondary) ? primary : secondary;
  const other = richer === primary ? secondary : primary;
  const merged: AlbumIndexRow = { ...other, ...richer };

  const richBd = Number(richer.bit_depth) || 0;
  const richSr = Number(richer.sample_rate) || 0;
  const otherBd = Number(other.bit_depth) || 0;
  const otherSr = Number(other.sample_rate) || 0;
  if (otherBd > richBd || (otherBd === richBd && otherSr > richSr)) {
    merged.bit_depth = other.bit_depth ?? richer.bit_depth;
    merged.sample_rate = other.sample_rate ?? richer.sample_rate;
    merged.format = other.format ?? richer.format;
  }
  if (!merged.format && other.format) merged.format = other.format;
  merged.track_count = Math.max(Number(richer.track_count) || 0, Number(other.track_count) || 0);
  merged.total_duration = Math.max(
    Number(richer.total_duration) || 0,
    Number(other.total_duration) || 0,
  );
  const canonicalId = resolveCanonicalAlbumTileId(richer) || resolveCanonicalAlbumTileId(other);
  if (canonicalId) {
    merged.album_key = canonicalId;
    merged.id = canonicalId;
  }
  return merged;
}

/**
 * Collapse duplicate catalog rows to one entry per albumIdentityKey.
 * Preserves first-seen order; when duplicates share an identity, keeps the richer row.
 */
export function dedupeAlbumIndexRows(albums: AlbumIndexRow[]): AlbumIndexDedupeResult {
  if (!albums?.length) return { albums: albums ?? [], collapsed: 0 };

  const order: string[] = [];
  const byKey = new Map<string, AlbumIndexRow>();
  let collapsed = 0;

  for (let i = 0; i < albums.length; i += 1) {
    const row = albums[i];
    if (!row) continue;
    let key = albumIdentityKey(row);
    if (!key) key = `__row_${i}`;

    const prev = byKey.get(key);
    if (prev) {
      collapsed += 1;
      byKey.set(key, mergeAlbumIndexRowPair(prev, row));
      continue;
    }
    order.push(key);
    byKey.set(key, row);
  }

  const identity = {
    albums: order.map((key) => byKey.get(key)!),
    collapsed,
  };
  const grouped = groupDiscFolderSiblings(identity.albums);
  return {
    albums: grouped.albums,
    collapsed: identity.collapsed + grouped.collapsed,
  };
}

/** React list key — identity when unique; index suffix only as last resort. */
export function getAlbumGridRowKey(album: AlbumIndexRow, listIndex: number): string {
  const identity = albumIdentityKey(album);
  return identity || `album-row-${listIndex}`;
}

export function reconcileAlbumsFromServer(prev: AlbumIndexRow[], incoming: AlbumIndexRow[]): AlbumIndexRow[] {
  if (!incoming?.length) return prev?.length ? prev : (incoming ?? []);
  if (!prev?.length) return incoming;
  const prevByKey = new Map<string, AlbumIndexRow>();
  for (const a of prev) {
    const k = albumIdentityKey(a);
    if (k) prevByKey.set(k, a);
  }
  return incoming.map((a) => {
    const k = albumIdentityKey(a);
    const prior = k ? prevByKey.get(k) : null;
    return prior ? { ...prior, ...a } : a;
  });
}

export function applyChunkDedupe(raw: AlbumIndexRow[]): AlbumIndexDedupeResult {
  return dedupeAlbumIndexRows(raw);
}

export function logBornToRunIdentityCounts(albums: AlbumIndexRow[]): void {
  const born = albums.filter((a) => normalizeTitle(a?.title ?? a?.album).includes('born to run'));
  if (born.length === 0) return;
  const byIdentity = new Map<string, number>();
  for (const row of born) {
    const key = albumIdentityKey(row) || '(empty)';
    byIdentity.set(key, (byIdentity.get(key) || 0) + 1);
  }
  const shared = [...byIdentity.entries()].filter(([, count]) => count > 1);
  if (shared.length === 0) return;
  console.log('[library-grid] born-to-run identity collision', {
    rows: born.length,
    identities: byIdentity.size,
    shared: Object.fromEntries(shared),
  });
}
